import { TerminalContent } from '@/components/terminal/TerminalContent';
import { TerminalHeader } from '@/components/terminal/TerminalHeader';
import {
  isStaticPhase,
  TerminalConfig,
  TerminalPhase,
  TerminalProps,
} from '@/components/terminal/types';
import { useEffect, useMemo, useState } from 'react';
import styles from './Terminal.module.css';

const defaultConfig = {
  title: 'EVA_TERMINAL_v2.4.1',
  typingSpeed: 30,
  phaseDelay: 500,
  matrixDelay: 2000,
  tableDelay: 2000,
  warningDelay: 2000,
};

const getPhaseDelay = (phase: TerminalPhase, config: TerminalConfig) => {
  if (phase.delay) {
    return phase.delay;
  }

  switch (phase.type) {
    case 'matrix':
      return config.matrixDelay;
    case 'table':
      return config.tableDelay;
    case 'warning':
      return config.warningDelay;
    default:
      return config.phaseDelay;
  }
};

export const Terminal = ({
  phases = [],
  config: partialConfig = {},
  className = '',
}: TerminalProps) => {
  const config = useMemo(
    () => ({ ...defaultConfig, ...partialConfig }) as TerminalConfig,
    [partialConfig]
  );
  const [currentPhase, setCurrentPhase] = useState(-1);
  const [typedText, setTypedText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const startTerminal = () => {
      setCurrentPhase(-1);
      setTypedText('');
      setIsActive(true);
      setTimeout(() => {
        setCurrentPhase(0);
      }, 500);
    };

    const handleSlideChange = (event: any) => {
      const currentSlide = event.currentSlide;

      if (currentSlide?.dataset?.hasTerminal === 'true' && phases?.length > 0) {
        startTerminal();
      } else {
        setIsActive(false);
        setCurrentPhase(-1);
        setTypedText('');
      }
    };

    const deck = document.querySelector('.reveal');
    if (deck) {
      deck.addEventListener('slidechanged', handleSlideChange);

      const currentSlide = deck.querySelector('.present');
      if (currentSlide) {
        handleSlideChange({ currentSlide });
      }
    }

    return () => {
      if (deck) {
        deck.removeEventListener('slidechanged', handleSlideChange);
      }
      setIsActive(false);
      setCurrentPhase(-1);
      setTypedText('');
    };
  }, [phases]);

  useEffect(() => {
    if (!isActive || currentPhase < 0 || currentPhase >= phases.length) return;

    const currentContent = phases[currentPhase];
    const delay = getPhaseDelay(currentContent, config);
    let timer: NodeJS.Timeout;

    if (isStaticPhase(currentContent.type)) {
      timer = setTimeout(() => {
        setCurrentPhase((prev) => prev + 1);
      }, delay);
    } else {
      let index = 0;
      const text = currentContent.content;

      const typingInterval = setInterval(() => {
        if (index <= text.length) {
          setTypedText(text.slice(0, index));
          index++;
        } else {
          clearInterval(typingInterval);
          timer = setTimeout(() => {
            setCurrentPhase((prev) => prev + 1);
            setTypedText('');
          }, delay);
        }
      }, config.typingSpeed);

      return () => {
        clearInterval(typingInterval);
        clearTimeout(timer);
      };
    }

    return () => clearTimeout(timer);
  }, [currentPhase, phases, config, isActive]);

  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 530);
    return () => clearInterval(cursorInterval);
  }, []);

  return (
    <>
      <div className={`fixed inset-0 ${styles.background}`} />
      <div className={`fixed inset-4 ${className} terminal-container`}>
        <div className={styles.container}>
          <div className={styles.scanlineContainer}>
            <div className={styles.scanline} />
          </div>

          <div className={styles.content}>
            <TerminalHeader title={config.title || defaultConfig.title} />

            <TerminalContent
              phases={phases}
              currentPhase={currentPhase}
              typedText={typedText}
              showCursor={showCursor}
            />
          </div>
        </div>
      </div>
    </>
  );
};
